import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Alert,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import * as React from "react";

import { Limit } from "@/shared/domain/limits";
import { useOrgLimits } from "@/shared/domain/limits/hooks/useOrgLimits";
import { useUpdateOrgLimit } from "@/shared/domain/limits/hooks/useUpdateOrgLimit";
import { Button } from "@/shared/foundation/primitives";

import {
  formatLimitQuantity,
  limitLabel,
  UNLIMITED_QUANTITY,
} from "./limitFormatting";
import styles from "./OrgLimitsDialog.module.css";

interface OrgLimitsDialogProps {
  orgId: string;
  open: boolean;
  onClose: () => void;
}

const QUANTITY_HELP = "Whole number; -1 or Unlimited for no cap";

interface EditingLimitRowProps {
  limit: Limit;
  isSaving: boolean;
  /** Called with the validated new quantity (possibly -1 for unlimited). */
  onSave: (quantity: number) => void;
  onCancel: () => void;
}

/**
 * The in-edit form of a limits table row. Mounted fresh each time an edit
 * begins, so the draft initializes from the row's current quantity without
 * any reset effects.
 */
function EditingLimitRow({
  limit,
  isSaving,
  onSave,
  onCancel,
}: EditingLimitRowProps) {
  const [draft, setDraft] = React.useState(String(limit.quantity));
  // The value the quantity field goes back to when "Unlimited" is unchecked.
  const [preUnlimitedDraft, setPreUnlimitedDraft] = React.useState<
    string | null
  >(null);

  const label = limitLabel(limit.resource_name);
  const parsed = Number(draft);
  const isValid =
    draft.trim() !== "" &&
    Number.isInteger(parsed) &&
    parsed >= UNLIMITED_QUANTITY;
  const unlimited = isValid && parsed === UNLIMITED_QUANTITY;

  function toggleUnlimited(checked: boolean) {
    if (checked) {
      setPreUnlimitedDraft(draft);
      setDraft(String(UNLIMITED_QUANTITY));
    } else {
      const restored = preUnlimitedDraft ?? "";
      setDraft(restored === String(UNLIMITED_QUANTITY) ? "" : restored);
    }
  }

  function handleSave() {
    if (isValid && !isSaving) {
      onSave(parsed);
    }
  }

  return (
    <TableRow>
      <TableCell>
        <div>{label}</div>
        <div className={styles.resourceName}>{limit.resource_name}</div>
      </TableCell>
      <TableCell>
        <div className={styles.editor}>
          <TextField
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
              } else if (e.key === "Escape") {
                // Cancel just this row's edit; without stopPropagation the
                // Dialog's own Escape handler would close the whole dialog.
                e.stopPropagation();
                onCancel();
              }
            }}
            error={!isValid}
            helperText={QUANTITY_HELP}
            disabled={isSaving || unlimited}
            inputProps={{
              inputMode: "numeric",
              "aria-label": `New value for ${label}`,
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={unlimited}
                onChange={(e) => toggleUnlimited(e.target.checked)}
                disabled={isSaving}
              />
            }
            label="Unlimited"
          />
        </div>
      </TableCell>
      <TableCell align="right" className={styles.actionsCell}>
        <IconButton
          size="small"
          aria-label={`Save ${label}`}
          onClick={handleSave}
          disabled={!isValid || isSaving}
        >
          {isSaving ? (
            <CircularProgress size={18} />
          ) : (
            <CheckIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Cancel ${label}`}
          onClick={onCancel}
          disabled={isSaving}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

/**
 * System-user-only viewer/editor for one org's effective limits.
 *
 * Rows come straight from the server's effective-limit set (tier defaults
 * merged with overrides) — there is no client-side list of resources, so new
 * backend limits show up here automatically with raw-name formatting until
 * `limitFormatting.ts` learns about them. Edits are one row at a time,
 * mirroring the backend's single-limit PUT.
 */
export function OrgLimitsDialog({
  orgId,
  open,
  onClose,
}: OrgLimitsDialogProps) {
  const limitsQuery = useOrgLimits(orgId, { enabled: open });
  const mutation = useUpdateOrgLimit();
  const [editingResource, setEditingResource] = React.useState<string | null>(
    null,
  );

  const sortedLimits = React.useMemo(
    () =>
      [...(limitsQuery.data ?? [])].sort((a, b) =>
        a.resource_name.localeCompare(b.resource_name),
      ),
    [limitsQuery.data],
  );

  function startEdit(resourceName: string) {
    mutation.reset();
    setEditingResource(resourceName);
  }

  function cancelEdit() {
    mutation.reset();
    setEditingResource(null);
  }

  function saveLimit(limit: Limit, quantity: number) {
    mutation.mutate(
      { ...limit, quantity },
      {
        onSuccess: () => {
          setEditingResource(null);
        },
      },
    );
  }

  const failedLimit = mutation.error !== null ? mutation.variables : undefined;

  let content: React.ReactNode;
  if (limitsQuery.isError) {
    content = (
      <Alert severity="error" className={styles.alert}>
        Failed to load limits: {limitsQuery.error.message}
      </Alert>
    );
  } else if (limitsQuery.isPending) {
    content = (
      <div className={styles.spinner}>
        <CircularProgress size={28} />
      </div>
    );
  } else {
    content = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Resource</TableCell>
            <TableCell>Limit</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedLimits.map((limit) => {
            if (limit.resource_name === editingResource) {
              return (
                <EditingLimitRow
                  key={limit.resource_name}
                  limit={limit}
                  isSaving={mutation.isPending}
                  onSave={(quantity) => saveLimit(limit, quantity)}
                  onCancel={cancelEdit}
                />
              );
            }

            const label = limitLabel(limit.resource_name);
            return (
              <TableRow key={limit.resource_name}>
                <TableCell>
                  <div>{label}</div>
                  <div className={styles.resourceName}>
                    {limit.resource_name}
                  </div>
                </TableCell>
                <TableCell>
                  <span title={String(limit.quantity)}>
                    {formatLimitQuantity(limit.resource_name, limit.quantity)}
                  </span>
                </TableCell>
                <TableCell align="right" className={styles.actionsCell}>
                  <IconButton
                    size="small"
                    aria-label={`Edit ${label}`}
                    onClick={() => startEdit(limit.resource_name)}
                    disabled={mutation.isPending}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  return (
    // "md" over "sm": with the full ~23-row limit set, an in-edit row (text
    // field + helper text + save/cancel buttons) plus the vertical scrollbar
    // overflows a 600px dialog sideways, hiding the save/cancel buttons
    // behind a horizontal scrollbar.
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Organization Limits
        <div className={styles.orgId}>{orgId}</div>
      </DialogTitle>
      <DialogContent>
        {mutation.error !== null && (
          <Alert severity="error" className={styles.alert}>
            Failed to update{" "}
            {failedLimit !== undefined
              ? limitLabel(failedLimit.resource_name)
              : "limit"}
            : {mutation.error.message}
          </Alert>
        )}
        {content}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
